import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { heapEvent } from '@equipmentshare/heaper'
import useInvoiceDetails from 'hooks/useInvoiceDetails'
import useCancelReservationHandler from 'views/inventory/effects/cancelReservation'
import { PurchaseOrderTargetType, ReservationTargetType } from '../../../../graphql'
import { StorePart } from '../../../../types/storePart'

/*
 * Pure-MUI commitment tables shown in the Commitments drawer — MUI replacements for the
 * EUI tables (AllocatedTable/OrderedTable/RentalsTable/OutgoingTable) so the new item
 * view uses no EUI. Behaviour is preserved: the reservation "Move to stock" action and
 * the target/invoice cell rendering. `useInvoiceDetails` and `useCancelReservationHandler`
 * are plain hooks and are reused as-is.
 */

type Reservation = NonNullable<StorePart['openReservations']>[number]
type PartsOrdered = NonNullable<StorePart['partsOrderedDetails']>[number]
type Rental = NonNullable<StorePart['rentalItems']>[number]
type OutgoingTransfer = NonNullable<StorePart['outgoingTransferDetails']>[number]

// --- Client-side search helpers (used by the Commitments drawer) ---
// Match against the synchronously-available fields. Invoice numbers are loaded async
// per-cell (useInvoiceDetails), so those aren't searchable — the target id is.

function reservationTargetText( reservation: Reservation ): string {
	if ( reservation.targetType === ReservationTargetType.FulfillmentOrder ) {
		return reservation.fulfillmentOrder ? `FO-${reservation.fulfillmentOrder.orderNumber}` : ''
	}
	if ( !reservation.targetType || !reservation.targetId ) {
		return ''
	}
	switch ( reservation.targetType ) {
		case ReservationTargetType.WorkOrder: return `WO-${reservation.targetId}`
		case ReservationTargetType.Invoice: return `invoice ${reservation.targetId}`
		case ReservationTargetType.Order: return `ORD-${reservation.targetId}`
		default: return `${reservation.targetId}`
	}
}

function orderTargetText( order: PartsOrdered ): string {
	if ( !order.targetType ) {
		return ''
	}
	switch ( order.targetType ) {
		case PurchaseOrderTargetType.WorkOrder: return `WO-${order.targetId}`
		case PurchaseOrderTargetType.Invoice: return `invoice ${order.targetId ?? ''}`
		case PurchaseOrderTargetType.Stock: return 'stock'
		default: return `${order.targetId ?? ''}`
	}
}

/** Each `matchX` expects `query` already trimmed and lower-cased. */
export function matchReservation( reservation: Reservation, query: string ): boolean {
	return `${reservationTargetText( reservation )} ${reservation.quantity}`.toLowerCase().includes( query )
}
export function matchOrder( order: PartsOrdered, query: string ): boolean {
	return `${order.purchaseOrderNumber} ${orderTargetText( order )} ${order.quantityOrdered}`.toLowerCase().includes( query )
}
export function matchRental( rental: Rental, query: string ): boolean {
	return `${rental.rentalId} ${rental.quantityOnRent}`.toLowerCase().includes( query )
}
export function matchOutgoing( transfer: OutgoingTransfer, query: string ): boolean {
	return `to-${transfer.transferOrderId} ${transfer.quantity}`.toLowerCase().includes( query )
}

function Text( { children }: { children: React.ReactNode } ) {
	return <Typography component="span" variant="body2">{children}</Typography>
}

function InvoiceCell( { targetId, withOrderedBy }: { targetId: number, withOrderedBy?: boolean } ) {
	const { loading, invoiceNo, orderedBy } = useInvoiceDetails( targetId )

	if ( loading ) {
		return <CircularProgress size={14} />
	}
	if ( !invoiceNo ) {
		return <Text>--</Text>
	}
	if ( withOrderedBy ) {
		return (
			<Stack spacing={0.25}>
				<Typography variant="body2">INV-{invoiceNo}</Typography>
				{orderedBy && <Typography variant="caption" color="text.secondary">{orderedBy}</Typography>}
			</Stack>
		)
	}
	return <Text>INV-{invoiceNo}</Text>
}

function ReservationTargetCell( { reservation }: { reservation: Reservation } ) {
	const { targetType, targetId, fulfillmentOrder } = reservation

	if ( targetType === ReservationTargetType.FulfillmentOrder ) {
		return fulfillmentOrder ? <Text>FO-{fulfillmentOrder.orderNumber}</Text> : null
	}
	if ( !targetType || !targetId ) {
		return null
	}
	switch ( targetType ) {
		case ReservationTargetType.WorkOrder:
			return <Text>WO-{targetId}</Text>
		case ReservationTargetType.Invoice:
			return <InvoiceCell targetId={targetId} withOrderedBy />
		case ReservationTargetType.Order:
			return <Text>ORD-{targetId}</Text>
		default:
			return <Text>{targetId}</Text>
	}
}

function OrderTargetCell( { order }: { order: PartsOrdered } ) {
	const { targetType, targetId } = order

	if ( !targetType ) {
		return null
	}
	switch ( targetType ) {
		case PurchaseOrderTargetType.WorkOrder:
			return <Text>WO-{targetId}</Text>
		case PurchaseOrderTargetType.Invoice:
			return <InvoiceCell targetId={targetId ?? 0} />
		case PurchaseOrderTargetType.Stock:
			return <Text>Stock</Text>
		default:
			return <Text>{targetId}</Text>
	}
}

enum RowStatus {
	Active,
	Cancelling,
	Cancelled,
}

export function AllocatedTable( { reservations }: { reservations: Reservation[] } ) {
	// `StorePartDetails` is the query backing this page — refetching `StoreParts` (the
	// inventory list's query) is a no-op here and logs an unknown-query warning.
	const cancelReservation = useCancelReservationHandler( { refetchQueries: [ 'StorePartDetails' ] } )
	// Only rows the user has acted on appear here; anything absent is still actionable.
	// Seeding from the initial `reservations` would strand rows that arrive on a refetch.
	const [ rowStatus, setRowStatus ] = useState<Record<number, RowStatus>>( {} )

	const handleCancel = ( id: number ) => async() => {
		heapEvent( 'Inventory - Inventory Table - Click - Move to Stock', { reservation_id: `${id}` } )
		setRowStatus( ( prev ) => ( { ...prev, [ id ]: RowStatus.Cancelling } ) )
		await cancelReservation( id )
		setRowStatus( ( prev ) => ( { ...prev, [ id ]: RowStatus.Cancelled } ) )
	}

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Item is for</TableCell>
						<TableCell>Qty</TableCell>
						<TableCell />
					</TableRow>
				</TableHead>
				<TableBody>
					{reservations.map( ( reservation ) => {
						const status = rowStatus[ reservation.reservationId ] ?? RowStatus.Active
						const canCancel = reservation.targetType !== ReservationTargetType.Order &&
							reservation.targetType !== ReservationTargetType.FulfillmentOrder
						return (
							<TableRow key={reservation.reservationId}>
								<TableCell><ReservationTargetCell reservation={reservation} /></TableCell>
								<TableCell>{reservation.quantity}</TableCell>
								<TableCell>
									{canCancel && (
										<Button
											size="small"
											variant="text"
											disabled={status !== RowStatus.Active}
											onClick={handleCancel( reservation.reservationId )}
										>
											{status === RowStatus.Active ? 'Move to stock'
												: status === RowStatus.Cancelling ? 'Moving…'
													: 'Cancelled'}
										</Button>
									)}
								</TableCell>
							</TableRow>
						)
					} )}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

export function OrderedTable( { orders }: { orders: PartsOrdered[] } ) {
	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>PO Number</TableCell>
						<TableCell>Qty Ordered</TableCell>
						<TableCell>Item Is For</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{orders.map( ( order ) => (
						<TableRow key={`${order.purchaseOrderNumber}-${order.targetType ?? ''}-${order.targetId ?? ''}`}>
							<TableCell>{order.purchaseOrderNumber}</TableCell>
							<TableCell>{order.quantityOrdered}</TableCell>
							<TableCell><OrderTargetCell order={order} /></TableCell>
						</TableRow>
					) )}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

export function RentalsTable( { rentals }: { rentals: Rental[] } ) {
	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Rental ID</TableCell>
						<TableCell>Qty</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rentals.map( ( rental ) => (
						<TableRow key={rental.id}>
							<TableCell>{rental.rentalId}</TableCell>
							<TableCell>{rental.quantityOnRent}</TableCell>
						</TableRow>
					) )}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

export function OutgoingTable( { transfers }: { transfers: OutgoingTransfer[] } ) {
	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Transfer</TableCell>
						<TableCell>Qty</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{transfers.map( ( transfer ) => (
						<TableRow key={transfer.transferOrderId}>
							<TableCell>
								<Link component={RouterLink} to={`/transfer-orders/${transfer.transferOrderId}`} underline="hover">
									TO-{transfer.transferOrderId}
								</Link>
							</TableCell>
							<TableCell>{transfer.quantity}</TableCell>
						</TableRow>
					) )}
				</TableBody>
			</Table>
		</TableContainer>
	)
}
